import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import GroupsApi from '../../api/groupsApi';
import { GroupSummary } from '../../api/homeApi';
import {
  DataPayload,
  EntityState,
  LoadDataAction,
  SortPayloadAction,
} from '../types';
import { Auth } from '../auth/types';

export type GroupsState = EntityState<GroupSummary>;

const initialState: GroupsState = {
  data: [],
  currentPage: 0,
  resultsPerPage: 50,
  totalPages: 1,
  sortColumn: 'name',
  sortDirection: 1,
};

const compareGroups = (
  a: GroupSummary,
  b: GroupSummary,
  column: keyof GroupSummary,
  direction: number
): number => {
  let result = 0;
  switch (column) {
    case 'name':
      result = a.name.localeCompare(b.name);
      break;
    case 'active':
      result = Number(a.active) - Number(b.active);
      break;
    case 'users':
      result = a.users - b.users;
      break;
    case 'shippers':
      result = a.shippers - b.shippers;
      break;
    case 'activeJourneys':
      result = a.activeJourneys - b.activeJourneys;
      break;
    case 'createdAt':
      result =
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      break;
    default:
      result = 0;
  }
  return result * direction;
};

const groupsSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    setGroups: (
      state,
      { payload }: PayloadAction<DataPayload<GroupSummary>>
    ) => {
      state.data = payload.entities;
      state.totalPages = payload.totalPages;
    },
    sortGroups: (state, { payload }: SortPayloadAction<GroupSummary>) => {
      state.sortColumn = payload.column;
      state.sortDirection = payload.direction;
    },
    setGroupsCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload;
    },
    resetGroupsPage: (state) => {
      state.sortColumn = initialState.sortColumn;
      state.sortDirection = initialState.sortDirection;
      state.currentPage = initialState.currentPage;
    },
  },
});

export const loadGroups: LoadDataAction<GroupSummary> = async (
  axios: AxiosInstance,
  auth: Auth,
  state: GroupsState
) => {
  const groups = await GroupsApi.getGroups(axios, auth);
  const sortedEntities = [...groups].sort((a, b) =>
    compareGroups(a, b, state.sortColumn, state.sortDirection)
  );
  return setGroups({
    entities: sortedEntities,
    totalPages: 1,
  });
};

export const { setGroups, sortGroups, setGroupsCurrentPage, resetGroupsPage } =
  groupsSlice.actions;

export default groupsSlice.reducer;
